import { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '@/lib/helpers';

const POLL_INTERVAL = 30000; // 30s polling
const SOUND_INTERVAL = 30000; // 30s sound alert

export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const audioRef = useRef(null);
  const soundTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/notifications`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data);
        if (data.length === 0) setDismissed(false);
      }
    } catch {}
  }, []);

  const playDing = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/ding.wav');
      audioRef.current.volume = 0.4;
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();
    pollTimerRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(pollTimerRef.current);
  }, [fetchNotifications]);

  // Refresh when browser tab becomes visible again
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchNotifications]);

  // Sound alert every 30s when there are unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;
  useEffect(() => {
    if (unreadCount > 0 && !dismissed) {
      soundTimerRef.current = setInterval(playDing, SOUND_INTERVAL);
      return () => clearInterval(soundTimerRef.current);
    } else {
      clearInterval(soundTimerRef.current);
    }
  }, [unreadCount, dismissed, playDing]);

  const dismiss = useCallback(() => setDismissed(true), []);
  const refresh = useCallback(() => {
    setDismissed(false);
    fetchNotifications();
  }, [fetchNotifications]);

  return { notifications, dismissed, dismiss, refresh, fetchNotifications };
}
